import { graphHooks } from '@tunasong/graph-lib/react'
import type { FeedItem } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import { UserAvatar, getProfileColor, Avatar } from '@tunasong/ui-lib'
import { useContext, type FC, type Ref } from 'react'
import { AssistantAvatarContext } from './assistant-avatar-context.js'
import ChatItem from './chat-item.js'
import styles from './chat-feed-item.module.css'

export interface ChatFeedItemProps {
  item: FeedItem
  highlight?: boolean
  ref: Ref<HTMLDivElement>
}

const userAvatarWithUser = (userId: string) => () => <UserAvatar className={styles.avatar} userId={userId} size="tiny" />
const DummyAvatar = () => <Avatar className={styles.avatar} />

export const ChatFeedItem: FC<ChatFeedItemProps> = props => {
  const { item, highlight = false, ref, ...restProps } = props
  const itemUserId = item.userId
  const { userId: currentUserId } = useCurrentUser()

  const { profile } = graphHooks.useProfile(item.userId)
  // Per-thread override for the assistant avatar. `AgentChat` (plugin-agent)
  // provides this so the agent's icon + color render for `userId === 'assistant'`
  // comments. plugin-chat cannot depend on plugin-agent (circular), so we
  // invert the dependency via context instead. Default `null` → fall back to
  // the generic DummyAvatar.
  const AssistantAvatar = useContext(AssistantAvatarContext)

  const isAssistant = item.userId === 'assistant'
  const resolveUserIcon = (): FC => {
    if (isAssistant && AssistantAvatar) {
      return AssistantAvatar
    }
    if (itemUserId && !isAssistant) {
      return userAvatarWithUser(itemUserId)
    }
    return DummyAvatar
  }

  const UserIcon = resolveUserIcon()
  const type = currentUserId === item.userId ? 'message' : item.userId === 'assistant' ? 'assistant' : 'response'
  const { colorName } = getProfileColor(profile)

  return (
    <ChatItem
      key={item.id}
      {...restProps}
      ref={ref}
      highlight={highlight}
      type={type}
      color={colorName}
      name={profile?.name ?? 'Unknown'}
      actions={item.actions}
      UserIcon={UserIcon}
      timestamp={item.createdAt}
    >
      {item.content}
    </ChatItem>
  )
}

export default ChatFeedItem
